import Vue from "vue";
import axios from "axios";
import {
  Tutor,
  Package,
  SelectOption,
  SelectOptionKey,
  AvailabilityEvent
} from "resources/assets/types";
import { formattedDate, localTime, localTimezone, currency } from "../../utils";
import sortBy from "lodash/sortBy";
import { DateTime } from "luxon";

import TutorDisplayName from "../../components/TutorDisplayName.vue";
import TutorCertification from "../../components/TutorCertification.vue";
import TutorSpecialAvailability from "../../components/TutorSpecialAvailability.vue";
import TutorHeadShot from "../../components/TutorHeadShot.vue";
import DateSelector from "../../components/DateSelector.vue";
import ChoicesField from "../../components/ChoicesField.vue";
import { TIME_FORMAT } from "../../constants";

export interface AvailabilitiesForDate {
  date: DateTime;
  availabilities: AvailabilityEvent[];
}

export const PageComponent = Vue.extend({
  props: {
    tutorId: {
      type: Number,
      required: true
    },
    isCurrentUserTutor: {
      type: Boolean,
      required: true
    }
  },

  created() {
    this.loadTutor();
    this.transitionToPendingDate();

    setInterval(() => {
      if (new Date().getSeconds() === 0) {
        this.updateDateWindow();
      }
    }, 1000);

    this.updateDateWindow();
  },

  watch: {
    pendingDate(pendingDate: DateTime | null) {
      this.availabilityEventId = null;

      if (!pendingDate) {
        // the pendingDate was cleared, usually meaning we've finished the transition
        // to a new month.
        return;
      }

      this.transitionToPendingDate();
    }
  },

  data() {
    return {
      minimumDate: localTime(),
      currentDate: null as DateTime | null,
      pendingDate: localTime() as DateTime | null,
      tutor: null as null | Tutor,
      availabilities: [] as AvailabilityEvent[],
      packages: [] as Package[],
      packageId: null as number | null,
      availabilityEventId: null as string | null,
      isShowingTutorNotice: false
    };
  },

  computed: {
    isValid(): boolean {
      return Boolean(this.packageId && this.availabilityEventId);
    },

    packageChoices(): SelectOption[] {
      return this.packages.map(pkg => ({
        key: pkg.id,
        value: pkg.id,
        label: pkg.description,
        meta: {
          rate: pkg.rate,
          rateAsCurrency: currency(pkg.rate)
        }
      }));
    },

    availabilityChoices(): SelectOption[] {
      if (this.currentDate === null) {
        return [];
      }

      return (
        this.availabilitiesByDate.find(
          ({ date }) => date.ordinal === (this.currentDate as DateTime).ordinal
        )?.availabilities || []
      )
        .map(a => ({
          id: a.id,
          availability_id: a.availability_id,
          startsAt: DateTime.fromISO(a.starts_at, { zone: "UTC" })
        }))
        .sort((a, b) => (a.startsAt < b.startsAt ? -1 : 1))
        .map(a => ({
          key: a.id,
          value: a.id,
          label: a.startsAt.setZone(localTimezone).toFormat(TIME_FORMAT)
        }));
    },

    availabilitiesByDate(): AvailabilitiesForDate[] {
      const summaries = this.availabilities.reduce(
        (acc: AvailabilitiesForDate[], availability) => {
          const date = DateTime.fromISO(availability.starts_at, { zone: "UTC" })
            .setZone(localTimezone)
            .startOf("day");
          const existingIndex = acc.findIndex(
            (m: AvailabilitiesForDate) => m.date.ordinal == date.ordinal
          );

          if (existingIndex >= 0) {
            acc[existingIndex].availabilities.push(availability);
          } else {
            acc.push({ date, availabilities: [availability] });
          }

          return acc;
        },
        []
      );

      return sortBy(summaries, "date");
    },

    datesWithAvailability(): DateTime[] {
      return this.availabilitiesByDate
        .map(summary => summary.date.setZone("UTC"))
        .sort();
    }
  },

  methods: {
    formattedDate,

    guardTutorsFromRequest(event: Event): boolean {
      if (this.isCurrentUserTutor) {
        event.preventDefault();

        this.isShowingTutorNotice = true;

        return false;
      }

      return true;
    },

    selectPackage(_: SelectOptionKey, key: SelectOptionKey, selected: boolean) {
      if (selected) {
        this.packageId = Number(key);
      } else {
        this.packageId = null;
      }
    },

    selectAvailability(
      _: SelectOptionKey,
      key: SelectOptionKey,
      selected: boolean
    ) {
      if (selected) {
        this.availabilityEventId = String(key);
      } else {
        this.availabilityEventId = null;
      }
    },

    async loadTutor() {
      const response = await axios.get(`/ajax/tutors/${this.tutorId}`, {
        params: { include: ["profile.packages"] }
      });

      const { packages, ...tutor } = response?.data?.data;

      this.packages = packages;
      this.tutor = tutor;
    },

    updateDateWindow() {
      this.minimumDate = localTime();

      if (this.currentDate && this.minimumDate >= this.currentDate) {
        this.currentDate = this.minimumDate;
      }
    },

    goToDate(date: DateTime) {
      this.pendingDate = date;
    },

    async transitionToPendingDate() {
      if (this.pendingDate === null) {
        return;
      }

      if (this.pendingDate.month !== this.currentDate?.month) {
        await this.loadAvailabilities(this.pendingDate);
        this.currentDate =
          this.datesWithAvailability?.[0]?.setZone(localTimezone) ||
          this.pendingDate;
      } else {
        this.currentDate = this.pendingDate;
      }

      this.pendingDate = null;
    },

    async loadAvailabilities(date: DateTime) {
      this.availabilities = [];
      const when = date.setZone("UTC");

      const {
        data: { data: availabilities }
      } = await axios.get(`/ajax/tutors/${this.tutorId}/availability`, {
        params: {
          year: when.year,
          month: when.month
        }
      });

      this.availabilities = availabilities;
    }
  },

  components: {
    TutorDisplayName,
    TutorCertification,
    TutorSpecialAvailability,
    TutorHeadShot,
    DateSelector,
    ChoicesField
  }
});
